import React from "react";
import ReactApexChart from "react-apexcharts";
import { Box, Typography } from "@mui/material";

const COLOR_PALETTE = [
  "#FF9800",
  "#FFEB3B",
  "#2196F3",
  "#9C27B0",
  "#EC407A",
  "#607D8B",
  "#795548",
  "#009688",
  "#E91E63",
  "#3F51B5",
  "#8BC34A",
  "#FFC107",
  "#673AB7",
];

const ErrorCodeDonut = ({ data }) => {
  if (!data || !data.error_codes) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          height: "100%",
          borderRadius: "8px",
          padding: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>No error data available</Typography>
      </Box>
    );
  }

  const errorCodes = data.error_codes;
  const errorTypes = [];
  let totalFailedCases = 0;

  Object.entries(errorCodes).forEach(([code, details]) => {
    if (!details) return;

    const count = details.failedTestCasesCount || 0;
    if (count > 0) {
      errorTypes.push({
        code,
        message: details.message || "Unknown error",
        count,
      });
      totalFailedCases += count;
    }
  });

  if (errorTypes.length === 0 || totalFailedCases === 0) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          height: "100%",
          borderRadius: "8px",
          padding: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>No error data available</Typography>
      </Box>
    );
  }

  const chartSeries = errorTypes.map((error) => error.count);
  const chartLabels = errorTypes.map(
    (error) =>
      `${error.code}: ${error.message.substring(0, 20)}${error.message.length > 20 ? "..." : ""}`,
  );

  const chartColors = errorTypes.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]);

  const chartOptions = {
    chart: {
      type: "donut",
      animations: { enabled: false },
      height: 350,
    },
    colors: chartColors,
    labels: chartLabels,
    dataLabels: { enabled: false },
    legend: {
      position: "bottom",
      offsetY: 0,
      horizontalAlign: "center",
      fontSize: "12px",
      itemMargin: {
        horizontal: 5,
        vertical: 2,
      },
      formatter: function (name, opts) {
        try {
          const value = opts.w.globals.series[opts.seriesIndex] || 0;
          const percentage = ((value / totalFailedCases) * 100).toFixed(1);
          const codeOnly = name.split(":")[0];
          return `${codeOnly}: ${percentage}%`;
        } catch (e) {
          return name;
        }
      },
    },
    tooltip: {
      y: { formatter: (val) => `${val} test cases` },
      theme: "light",
      custom: function ({ series, seriesIndex, w }) {
        const label = w.config.labels[seriesIndex];
        return `<div class="apexcharts-tooltip-title" style="font-weight:bold">${label}</div>
                <div class="apexcharts-tooltip-series-group">
                  <span>${series[seriesIndex]} test cases</span>
                </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { width: 200 },
          legend: { show: false },
        },
      },
    ],
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
        },
      },
    },
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        height: "100%",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          color: "#232D3D",
          fontSize: "16px",
          fontWeight: 600,
          textAlign: "center",
          marginBottom: "8px",
        }}
      >
        Error Distribution
      </Typography>
      <Box sx={{ flex: 1, minHeight: "300px" }}>
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="donut"
          height="100%"
          width="100%"
        />
      </Box>
    </Box>
  );
};

export default ErrorCodeDonut;
